import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Broom, X, CheckCircle, WarningCircle, Trash } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { API } from '@/App';

const FLAG_LABELS = {
  email_pattern: 'Email pattern',
  name_pattern: 'Name pattern',
  subaddressed_email: 'Sub-addressed (+tag)',
  never_logged_in_no_ticket: 'Never logged in',
  unverified_no_ticket: 'Unverified',
  kyc_not_started_no_ticket: 'No KYC',
};

const PILL_BASE = 'rounded-lg border border-[#cba135]/15 bg-[#1a1508] p-5 shadow-[0_8px_32px_rgba(0,0,0,0.6)]';

function fmt(s) {
  if (!s) return '—';
  try { return new Date(s).toISOString().replace('T', ' ').slice(0, 19); } catch { return s; }
}

export default function TestAccountsCleanupPanel({ token, onMutated }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyIds, setBusyIds] = useState({});

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/users/audit/test-accounts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (err) {
      toast.error(`Failed to load test accounts: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const deleteOne = async (candidate) => {
    const expected = `DELETE_USER_${candidate.email}`;
    if (!window.confirm(
      `Hard-delete account "${candidate.email}"?\n\n` +
      `This is IRREVERSIBLE. KYC + payment intents (pending) will be removed; ledger entries are kept for AML.\n\n` +
      `Click OK to proceed (confirmation phrase will be auto-supplied).`
    )) return;
    setBusyIds((b) => ({ ...b, [candidate.id]: true }));
    try {
      const r = await fetch(`${API}/admin/users/${candidate.id}/delete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: expected, reason: 'Pre-launch test account cleanup' }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      toast.success(`Deleted ${candidate.email}`);
      await refresh();
      onMutated && onMutated();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusyIds((b) => { const c = { ...b }; delete c[candidate.id]; return c; });
    }
  };

  const deleteAllSafe = async () => {
    const deletable = (data?.candidates || []).filter((c) => c.canHardDelete);
    if (deletable.length === 0) {
      toast.info('No safely-deletable test accounts');
      return;
    }
    if (!window.confirm(
      `⚠ Hard-delete ${deletable.length} suspected test account(s)?\n\n` +
      `Only accounts with NO tickets in the active draw and NO pending withdrawals will be deleted.\n` +
      `This is IRREVERSIBLE. Ledger entries are kept for AML.\n\n` +
      `Type the exact phrase "DELETE_ALL_TEST" in the next prompt to confirm.`
    )) return;
    const typed = window.prompt('Type DELETE_ALL_TEST to proceed:');
    if (typed !== 'DELETE_ALL_TEST') {
      toast.info('Bulk delete cancelled');
      return;
    }
    let ok = 0, fail = 0;
    for (const c of deletable) {
      setBusyIds((b) => ({ ...b, [c.id]: true }));
      try {
        const r = await fetch(`${API}/admin/users/${c.id}/delete`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ confirm: `DELETE_USER_${c.email}`, reason: 'Pre-launch bulk test cleanup' }),
        });
        if (r.ok) ok += 1; else fail += 1;
      } catch { fail += 1; }
      setBusyIds((b) => { const x = { ...b }; delete x[c.id]; return x; });
    }
    toast[fail > 0 ? 'warning' : 'success'](`Bulk done: ${ok} deleted, ${fail} failed`);
    await refresh();
    onMutated && onMutated();
  };

  return (
    <>
      <Button
        data-testid="test-accounts-cleanup-button"
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="border-[#cba135]/30 text-[#cba135] hover:bg-[#cba135]/10"
      >
        <Broom size={16} weight="duotone" className="mr-2" />
        Pre-launch cleanup
      </Button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-[#0e0b04]/90 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
              className="bg-[#1a1508] border border-[#cba135]/30 rounded-xl shadow-[0_20px_60px_rgba(0,0,0,0.8)] w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden"
              data-testid="test-cleanup-modal"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#cba135]/15">
                <div className="flex items-center gap-3">
                  <Broom size={24} weight="duotone" className="text-[#cba135]" />
                  <div>
                    <h2 className="heading-font text-lg text-[#cba135]">Pre-launch test account cleanup</h2>
                    <p className="text-xs text-[#cba135]/55 mt-0.5">
                      Suspected test/dev accounts surfaced by automatic heuristics. Always review before deleting.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="p-2 text-[#cba135]/60 hover:text-[#cba135] hover:bg-[#cba135]/10 rounded transition-colors"
                  aria-label="Close"
                >
                  <X size={22} />
                </button>
              </div>

              <div className="px-6 py-4 border-b border-[#cba135]/10 bg-[#0e0b04]/40 flex flex-wrap items-center gap-3">
                {loading ? (
                  <p className="text-sm text-[#cba135]/70">Loading…</p>
                ) : data ? (
                  <>
                    <div className={`${PILL_BASE} py-2 px-3`}>
                      <p className="text-[10px] uppercase tracking-widest text-[#a8872b]">Candidates</p>
                      <p className="heading-font text-xl text-[#cba135] tabular-nums">{data.totalCandidates}</p>
                    </div>
                    <div className={`${PILL_BASE} py-2 px-3`}>
                      <p className="text-[10px] uppercase tracking-widest text-[#a8872b]">Safely deletable</p>
                      <p className="heading-font text-xl text-green-300 tabular-nums">{data.deletableCount}</p>
                    </div>
                    <div className={`${PILL_BASE} py-2 px-3`}>
                      <p className="text-[10px] uppercase tracking-widest text-[#a8872b]">Blocked</p>
                      <p className="heading-font text-xl text-orange-300 tabular-nums">{data.blockedCount}</p>
                    </div>
                    <div className="ml-auto flex gap-2">
                      <Button size="sm" variant="outline" onClick={refresh}
                        className="border-[#cba135]/30 text-[#cba135] hover:bg-[#cba135]/10">
                        Refresh
                      </Button>
                      <Button size="sm" onClick={deleteAllSafe} disabled={data.deletableCount === 0}
                        data-testid="bulk-delete-safe"
                        className="bg-red-700/30 border border-red-500/40 text-red-100 hover:bg-red-700/50 font-semibold">
                        <Trash size={16} weight="duotone" className="mr-2" />
                        Delete all safe ({data.deletableCount})
                      </Button>
                    </div>
                  </>
                ) : null}
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4">
                {!loading && data?.candidates?.length === 0 && (
                  <p className="text-center text-[#cba135]/60 py-12 text-sm">
                    No suspected test accounts found. Your production DB is clean ✨
                  </p>
                )}
                {data?.candidates?.length > 0 && (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-widest text-[#a8872b] border-b border-[#cba135]/10">
                        <th className="py-2 pr-3">Email</th>
                        <th className="py-2 pr-3">Created</th>
                        <th className="py-2 pr-3">Last login</th>
                        <th className="py-2 pr-3">Status</th>
                        <th className="py-2 pr-3">Flags</th>
                        <th className="py-2 pr-3 text-right">Tickets</th>
                        <th className="py-2 pr-3 text-right">Paid pi</th>
                        <th className="py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.candidates.map((c) => (
                        <tr key={c.id} className="border-b border-[#cba135]/5 hover:bg-[#cba135]/5"
                          data-testid={`test-account-row-${c.id}`}>
                          <td className="py-2 pr-3">
                            <div className="font-medium text-[#cba135]">{c.email}</div>
                            <div className="text-xs text-[#cba135]/45 truncate max-w-[260px]">{c.name || '—'}</div>
                          </td>
                          <td className="py-2 pr-3 text-xs text-[#cba135]/70 font-mono">{fmt(c.createdAt)}</td>
                          <td className="py-2 pr-3 text-xs text-[#cba135]/70 font-mono">{fmt(c.lastLoginAt)}</td>
                          <td className="py-2 pr-3 text-xs">
                            <div className="text-[#cba135]/80">{c.accountStatus}</div>
                            <div className="text-[#cba135]/45">{c.verificationStatus || '—'}</div>
                          </td>
                          <td className="py-2 pr-3">
                            <div className="flex flex-wrap gap-1">
                              {c.flags.map((f) => (
                                <span key={f} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#cba135]/10 text-[#cba135]/80 border border-[#cba135]/20">
                                  {FLAG_LABELS[f] || f}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums text-[#cba135]">{c.ticketsTotal}</td>
                          <td className="py-2 pr-3 text-right tabular-nums text-[#cba135]">{c.paidPaymentIntents}</td>
                          <td className="py-2 text-right">
                            {c.canHardDelete ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => deleteOne(c)}
                                disabled={!!busyIds[c.id]}
                                data-testid={`delete-test-account-${c.id}`}
                                className="border-red-500/30 text-red-200 hover:bg-red-900/30"
                              >
                                {busyIds[c.id] ? '…' : (<><Trash size={14} weight="duotone" className="mr-1" />Delete</>)}
                              </Button>
                            ) : (
                              <div className="flex items-center justify-end gap-1.5 text-xs text-orange-300/80" title={c.blockReason}>
                                <WarningCircle size={14} weight="duotone" />
                                <span className="text-[10px]">Blocked</span>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="px-6 py-3 border-t border-[#cba135]/10 bg-[#0e0b04]/40">
                <p className="text-[11px] text-[#cba135]/55 leading-relaxed">
                  <CheckCircle size={11} weight="duotone" className="inline mr-1 text-green-400" />
                  Ledger entries, transactions, paid tickets and audit logs are <span className="text-[#e6be4b]">preserved for AML compliance</span> on every delete.
                  Admins are always excluded from this list. Accounts with active-draw tickets are blocked from deletion.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
